import FormWatcher from '@form/FormWatcher';
import { useApi } from '@hooks/useApi';
import useCurrency from '@hooks/useCurrency';
import { Blockquote, Spacer } from '@ui';
import { useClubSlug } from 'components/Clubs/hooks';
import { Form } from 'components/Form/base/Form';
import { FormInput } from 'components/Form/next';
import Loader from 'components/Loader';
import SelectComponent from 'components/SelectComponent';
import Joi from 'joi';
import { useState } from 'react';
import { FieldValues } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { SWRConfiguration } from 'swr';
import { InlineSwitch } from '~/components/ui/InlineSwitch';
import usePersistentState from '../../../hooks/usePersistentState';
import { WYSIWYGText } from '../../WYSIWYGText';
import { useInvoice, useIssuers, useIssuerSetting, usePaymentTerms } from '../data';
import { isLedgerDecisionError, useDocumentMandant, useLedgerDecision } from '../hooks/useLedgerDecision';
import type { LedgerDecision } from '../LedgerDecisionDialog';
import { UserInvoice } from '../types';
import { InvoiceCreditsFields } from './InvoiceCreditsFields';
import { AccountMandantProvider } from '../Account/AccountFormInput';
import { InvoiceItemsFields } from './InvoiceItemsFields';
import { InvoiceNumberInput } from './InvoiceNumberInput';
import { InvoicePriceComponent } from './InvoicePriceComponent';
import { InvoiceRecipientNameField } from './InvoiceRecipientNameField';

type PartialInvoice = Partial<UserInvoice>;

interface Props {
    invoice?: PartialInvoice;
    callback?: (newInvoice: UserInvoice) => any;
    isCredit?: boolean;
    overrideAllowEditing?: boolean;
}

export const InvoiceForm = ({
    invoice: _invoice,
    callback,
    isCredit,
    /* eslint-disable */
    overrideAllowEditing: _overrideAllowEditing = false
}: Props) => {
    const { t } = useTranslation('invoice');
    const clubSlug = useClubSlug();
    const api = useApi();
    const { currencies } = useCurrency();
    const [isLoading, setIsLoading] = useState(false);
    const [currentIssuer, setCurrentIssuer] = useState(_invoice?.issuer);
    const [showLedgerFields, setShowLedgerFields] = usePersistentState<boolean>('showLedgerFields', false);
    const { data: paymentTermData, isLoading: paymentTermIsLoading } = usePaymentTerms(clubSlug);

    const swrConfig: SWRConfiguration = {
        revalidateIfStale: false,
        revalidateOnFocus: false,
        revalidateOnReconnect: false,
        revalidateOnMount: true
    };

    const { issuers, isLoading: issuersIsLoading } = useIssuers(clubSlug, false, {
        ...swrConfig,
        onSuccess(data) {
            if (!currentIssuer && !_invoice?.id && data.data?.length) {
                setCurrentIssuer(data.data[0]);
            }
        }
    });
    const { issuerSetting, isLoading: issuerSettingIsLoading } = useIssuerSetting(clubSlug, currentIssuer, swrConfig);
    const ledgerDecision = useLedgerDecision(useDocumentMandant(clubSlug, currentIssuer?.mandant_id));
    const {
        invoice,
        // isLoading: invoiceIsLoading,
        isValidating: invoiceIsValidating,
        mutate: mutateInvoice
    } = useInvoice(_invoice?.id, {
        ...swrConfig,
        onSuccess(data) {
            if (!currentIssuer) setCurrentIssuer(data?.data.issuer);
        }
    });

    // Lexoffice rule: drafts and finalized-but-unpaid invoices are editable; a payment/bank
    // association hard-locks editing until the association is removed.
    const isFinalized = Boolean(_invoice?.number);
    const hasPayment = Boolean(_invoice?.payments_sum_amount) || Boolean(_invoice?.payed_at);
    const allowEditing = !hasPayment;
    const isPreloading = issuersIsLoading || issuerSettingIsLoading || invoiceIsValidating || paymentTermIsLoading;

    const schema = Joi.object({
        title: Joi.string().max(255).allow('', null).label(t('invoiceForm.fields.title')),
        header_title: Joi.string()
            .max(255)
            .allow('', null)
            .label(isCredit ? t('invoiceForm.fields.creditHeaderTitle') : t('invoiceForm.fields.invoiceHeaderTitle')),
        header_entry: Joi.string().allow('', null).label(t('invoiceForm.fields.headerEntry')),
        line0: Joi.string().max(255).required().label(t('invoiceForm.fields.name')),
        line1: Joi.string().max(255).allow('', null).label(t('invoiceForm.fields.addressLine2')),
        line2: Joi.string().max(255).allow('', null).label(t('invoiceForm.fields.addressLine3')),
        city: Joi.string().max(255).allow('', null).label(t('invoiceForm.fields.city')),
        postcode: Joi.string().max(255).allow('', null).label(t('invoiceForm.fields.postcode')),
        date: Joi.date().required().label(t('invoiceForm.fields.thisField')),
        due_date: Joi.date().allow(null, '').label(t('invoiceForm.fields.dueDate')),
        iban: Joi.string().allow('', null).label(t('invoiceForm.fields.iban')),
        bic: Joi.string().allow('', null).label(t('invoiceForm.fields.bic')),
        debtor: Joi.string().allow('', null).label(t('invoiceForm.fields.debtorNumber')),
        currency: Joi.string()
            .valid(...currencies.map((c) => c.code))
            .label(t('invoiceForm.fields.currency')),
        items: Joi.array()
            .items(
                Joi.object({
                    description: Joi.string().required().label(t('invoiceForm.fields.description')),
                    description_long: Joi.string().allow('', null).label(t('invoiceForm.fields.descriptionLong')),
                    amount: Joi.number().min(0).max(10000).required().label(t('invoiceForm.fields.amount')),
                    taxes: Joi.number().allow(null, '').min(0).max(100).label(t('invoiceForm.fields.taxes')),
                    price_per_item: Joi.number().min(0).required().label(t('invoiceForm.fields.pricePerItem'))
                }).unknown()
            )
            .min(1)
            .max(500)
            .required()
            .label(t('invoiceForm.fields.items')),
        credits: Joi.array()
            .items(
                Joi.alternatives().conditional('percentage', {
                    then: Joi.object({
                        description: Joi.string().required().max(255).label(t('invoiceForm.fields.description')),
                        amount: Joi.number().min(0).max(100).required().label(t('invoiceForm.fields.amount'))
                    }).unknown(),
                    otherwise: Joi.object({
                        description: Joi.string().required().max(255).label(t('invoiceForm.fields.description')),
                        amount: Joi.number().min(0).max(100000).required().label(t('invoiceForm.fields.amount'))
                    }).unknown()
                })
            )
            .max(500)
            .label(t('invoiceForm.fields.credits')),
        taxes: Joi.number().min(0).required().label(t('invoiceForm.fields.taxesShort')),
        remarks: Joi.string().max(5000).allow('', null),
        email: Joi.string()
            .email({ tlds: { allow: false } })
            .allow('', null)
            .max(255)
            .label(t('invoiceForm.fields.recipientEmail'))
    }).unknown();

    const handleSubmit = async (data: FieldValues, event: any, decided?: LedgerDecision) => {
        const isNew = !invoice;
        let ledger_decision = decided;

        // New invoices are finalized right away, so a date before the accounting start needs a decision first.
        if (isNew && !ledger_decision) {
            const asked = await ledgerDecision.resolve(data.date);
            if (asked === null) return;
            ledger_decision = asked;
        }

        setIsLoading(true);
        return api({
            url: 'invoice/' + (invoice ? invoice.id : clubSlug),
            method: invoice ? 'PUT' : 'POST',
            data: {
                ...(invoice || {}),
                ...data,
                items: data.items?.map((item: FieldValues) => ({
                    ...item,
                    taxes: item.taxes === '' ? null : item.taxes
                })),
                is_credit: isCredit,
                user_invoice_setting_id: issuerSetting?.id,
                ...(isNew && { finalize: true }),
                ...(ledger_decision && { ledger_decision })
            },
            muteWhen: (response) => isNew && !ledger_decision && isLedgerDecisionError(response),
            onFailure: (response) => {
                setIsLoading(false);
                if (isNew && !ledger_decision) {
                    void ledgerDecision
                        .retry(response)
                        .then((retried) => retried && handleSubmit(data, event, retried));
                }
            },
            onSuccess: ({ data: savedInvoice }) => {
                setIsLoading(false);
                if (callback && event.nativeEvent.submitter.id !== 'invoice_presubmit') {
                    callback(savedInvoice);
                } else {
                    void mutateInvoice(savedInvoice);
                }
            }
        });
    };

    const formInputProps: {
        withoutShifting: boolean;
        size: 'sm';
    } = {
        withoutShifting: true,
        size: 'sm'
    };

    return (
        <Loader isLoading={isPreloading}>
            <div>
                {!allowEditing ? (
                    <Blockquote color="error" className="!tw-my-0">
                        {t('invoiceForm.warnings.lockedByPayment')}
                    </Blockquote>
                ) : (
                    <>
                        {isFinalized && (
                            <Blockquote color="warning" className="tw-mb-4">
                                {t('invoiceForm.warnings.finalizedEdit')}
                            </Blockquote>
                        )}
                        <SelectComponent
                            buttonProps={{ css: { mb: '$8' } }}
                            options={issuers?.map((iss) => ({ value: String(iss.id), label: iss.title }))}
                            value={String(currentIssuer?.id)}
                            onChange={(option) => setCurrentIssuer(issuers.find((iss) => String(iss.id) === option))}
                        />
                    </>
                )}
                {!currentIssuer ? (
                    <Blockquote className="tw-mt-4">{t('invoiceForm.selectIssuerFirst')}</Blockquote>
                ) : (
                    <Form
                        onSubmit={handleSubmit}
                        validationSchema={schema}
                        setDefaultValuesOnRerender={false}
                        defaultValues={{
                            date: new Date(),
                            currency: issuerSetting?.currency ?? 'EUR',
                            taxes: isCredit ? 0 : (issuerSetting?.default_tax_rate ?? 19),
                            ...(invoice || _invoice || {})
                        }}
                    >
                        <div className="tw-flex tw-gap-5">
                            <div className="tw-flex-1">
                                <div className="tw-grid tw-grid-cols-12 tw-gap-y-3">
                                    <div className="tw-col-span-5">
                                        <FormInput
                                            disabled={!allowEditing}
                                            placeholder={t('invoiceForm.fields.customerNumber')}
                                            {...formInputProps}
                                            name="party_id"
                                        />
                                        <InvoiceRecipientNameField
                                            disabled={!allowEditing}
                                            placeholder={t('invoiceForm.fields.name')}
                                            {...formInputProps}
                                            name="line0"
                                        />
                                        <FormInput
                                            {...formInputProps}
                                            disabled={!allowEditing}
                                            placeholder={t('invoiceForm.fields.streetAndNumber')}
                                            name="line1"
                                        />
                                        <FormInput
                                            {...formInputProps}
                                            disabled={!allowEditing}
                                            placeholder={t('invoiceForm.fields.addressLine2')}
                                            name="line2"
                                        />
                                        <FormInput
                                            {...formInputProps}
                                            disabled={!allowEditing}
                                            placeholder={t('invoiceForm.fields.cityExample')}
                                            name="city"
                                        />
                                        <FormInput
                                            {...formInputProps}
                                            disabled={!allowEditing}
                                            placeholder={t('invoiceForm.fields.postcode')}
                                            name="postcode"
                                        />
                                    </div>
                                    <div className="tw-col-span-2" />
                                    <div className="tw-col-span-5">
                                        <FormInput
                                            name="title"
                                            placeholder={t('invoiceForm.fields.internalTitle')}
                                            helperText={t('invoiceForm.fields.internalTitleHelper')}
                                        />
                                        {!isCredit && (
                                            <>
                                                <FormInput
                                                    {...formInputProps}
                                                    helperText={t('invoiceForm.fields.purposeHelper', {
                                                        issuer: currentIssuer.title,
                                                        number: invoice?.number || '-'
                                                    })}
                                                    placeholder={t('invoiceForm.fields.sepaPurpose')}
                                                    name="purpose"
                                                />
                                                <FormInput.IBAN {...formInputProps} name="iban" placeholder={t('invoiceForm.fields.iban')} />
                                                <FormInput
                                                    {...formInputProps}
                                                    placeholder={t('invoiceForm.fields.bic')}
                                                    name="bic"
                                                    helperText={t('invoiceForm.fields.bicHelper')}
                                                    secure
                                                />
                                            </>
                                        )}
                                        <FormInput
                                            {...formInputProps}
                                            placeholder={t('invoiceForm.fields.debtorNumber')}
                                            name="debtor"
                                            disabled={!allowEditing}
                                            helperText={t('invoiceForm.fields.debtorNumberHelper')}
                                        />
                                    </div>
                                    <div className="tw-col-span-5">
                                        <Spacer y={1.5} />

                                        <FormInput
                                            {...formInputProps}
                                            name="email"
                                            placeholder={t('invoiceForm.fields.recipientEmail')}
                                        />
                                        {!_invoice?.number && (
                                            <InvoiceNumberInput
                                                {...formInputProps}
                                                issuer={currentIssuer}
                                                clubSlug={isLoading ? null : clubSlug}
                                                name="number"
                                                manual={invoice !== undefined}
                                                label={isCredit ? t('invoiceForm.fields.creditNumber') : t('invoiceForm.fields.invoiceNumber')}
                                                isCredit={isCredit}
                                            />
                                        )}
                                        <FormInput
                                            {...formInputProps}
                                            name="header_title"
                                            placeholder={isCredit ? t('invoiceForm.fields.creditHeaderTitle') : t('invoiceForm.fields.invoiceHeaderTitle')}
                                        />
                                    </div>
                                    <div className="tw-col-span-2" />
                                    <div className="tw-col-span-5">
                                        <FormInput.DatePicker
                                            {...formInputProps}
                                            placeholder={isCredit ? t('invoiceForm.fields.creditDate') : t('invoiceForm.fields.invoiceDate')}
                                            name="date"
                                            disabled={!allowEditing}
                                        />
                                        <FormInput.Select
                                            disableCustomValue
                                            name="payment_term_id"
                                            options={[
                                                {
                                                    label: t('invoiceForm.fields.customPaymentTerm'),
                                                    key: ''
                                                },
                                                ...(paymentTermData?.data?.map((pt) => ({
                                                    label: `${pt.key} - ${pt.title}`,
                                                    key: pt.id
                                                })) ?? [])
                                            ]}
                                        />
                                        <FormWatcher name="payment_term_id">
                                            {(payment_term) =>
                                                !payment_term ? (
                                                    <FormInput.DatePicker
                                                        {...formInputProps}
                                                        placeholder={t('invoiceForm.fields.dueDate')}
                                                        name="due_date"
                                                        disabled={!allowEditing}
                                                    />
                                                ) : null
                                            }
                                        </FormWatcher>

                                        <FormInput.DatePicker
                                            {...formInputProps}
                                            placeholder={t('invoiceForm.fields.deliveryDate')}
                                            name="delivery_date"
                                            disabled={!allowEditing}
                                        />
                                    </div>
                                    <div className="tw-col-span-12">
                                        <FormInput
                                            {...formInputProps}
                                            name="header_entry"
                                            placeholder={t('invoiceForm.fields.headerEntry')}
                                        />
                                        <div className="tw-mb-2 tw-flex tw-items-center tw-justify-end tw-gap-4">
                                            <span className="tw-block tw-text-accents-6">
                                                {t('invoiceForm.showAdvancedItems')}
                                            </span>
                                            <InlineSwitch
                                                className="!tw-mb-1"
                                                onChange={(e) => setShowLedgerFields(e)}
                                                checked={!!showLedgerFields}
                                            />
                                        </div>
                                        <AccountMandantProvider value={currentIssuer?.mandant_id}>
                                            <InvoiceItemsFields
                                                showLedgerFields={showLedgerFields}
                                                showDescription={showLedgerFields}
                                                name="items"
                                                allowEditing={allowEditing}
                                            />
                                        </AccountMandantProvider>
                                        <Spacer y={4} />
                                        {!isCredit && (
                                            <InvoiceCreditsFields name="credits" allowEditing={allowEditing} />
                                        )}
                                        <Spacer y={1} />
                                    </div>
                                    <div className="tw-col-span-7" />
                                    <div className="tw-col-span-5">
                                        <FormInput.Select
                                            name="currency"
                                            options={currencies.map((c) => ({ key: c.code, label: c.name }))}
                                        />
                                        <FormInput
                                            type="number"
                                            min={0}
                                            max={100}
                                            name="taxes"
                                            placeholder={t('invoiceForm.fields.vat')}
                                            labelRight="%"
                                            disabled={!allowEditing}
                                        />
                                        {issuerSetting?.small_business && !isCredit && (
                                            <p className="tw-text-sm tw-text-gray-500">
                                                {t('invoiceForm.smallBusinessHint')}
                                            </p>
                                        )}
                                        <InvoicePriceComponent
                                            invoice={invoice}
                                            setting={issuerSetting}
                                            isCredit={isCredit}
                                        />
                                    </div>
                                    <div className="tw-col-span-12">
                                        <FormInput.WYSIWYG label={t('invoiceForm.fields.remarks')} name="remarks" />
                                        <FormWatcher name="payment_term_id">
                                            {(payment_term) => {
                                                const remarks = paymentTermData?.data?.find(
                                                    (pt) => pt.id == payment_term
                                                )?.remarks;
                                                return payment_term && remarks ? (
                                                    <div className="tw-card-flat">
                                                        <h5 className="tw-text-lg tw-font-semibold">
                                                            {t('invoiceForm.paymentTermsHeading')}
                                                        </h5>
                                                        <WYSIWYGText className="tw-text-accents-6">
                                                            {remarks}
                                                        </WYSIWYGText>
                                                    </div>
                                                ) : null;
                                            }}
                                        </FormWatcher>
                                    </div>
                                </div>
                                {/*{_invoice &&
                                        <FormInput.Submit id="invoice_presubmit" isLoading={isLoading}>
                                            Vorschau aktualisieren
                                        </FormInput.Submit>}*/}
                                <FormInput.Submit isLoading={isLoading}>{t('invoiceForm.common.save')}</FormInput.Submit>
                            </div>
                            {/*{_invoice && <div className="tw-flex-1">
                                    <LivePreview invoice={invoice} />
                                </div>}*/}
                        </div>
                    </Form>
                )}
            </div>
        </Loader>
    );
};

export default InvoiceForm;
